/**
 * KinsCircle Phase 6C — Semantic Lab (owner/admin only).
 *
 * Read-only diagnostics + sandbox at /admin/semantic. Everything is
 * localStorage-only:
 *   - never writes to Supabase
 *   - never reads real family memories by default (sandbox uses fake data)
 *   - never exposes API keys (no API calls happen here at all)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Layers,
  Search,
  RefreshCcw,
  Trash2,
  ShieldAlert,
  Database,
} from 'lucide-react';
import {
  snapshotSemanticFlags,
  setSemanticFlagLocal,
  isSemanticIndexEnabled,
  type SemanticFlagKey,
} from '@/lib/ai/semanticFeatureFlags';
import {
  buildSemanticMemoryIndex,
  clearSemanticIndex,
  readSemanticIndexStats,
  searchSemanticMemories,
  buildMemoryClusters,
} from '@/lib/ai/semanticIndexer';
import { buildHistorianDashboard } from '@/lib/ai/historianEngine';
import type { SemanticMemoryRow } from '@/lib/ai/memoryEmbeddingAdapter';

// ── Sample/fake data — never reads real family memories. ────────────────────
const SAMPLE_MEMORIES: SemanticMemoryRow[] = [
  {
    id: 'sample-1',
    title: 'Christmas at Grandma\'s',
    caption:
      'The whole family gathered at Grandma Rose\'s house for Christmas. The kids ran around with new toys and we sang carols by the tree.',
    date: '2018-12-25',
    taggedNames: ['Grandma Rose', 'Mom', 'Dad', 'Cousin Nia'],
    personId: null,
  },
  {
    id: 'sample-2',
    title: 'Christmas Morning',
    caption:
      'Another beautiful Christmas morning. The cousins opened presents and Dad made his famous pancakes.',
    date: '2020-12-25',
    taggedNames: ['Dad', 'Cousin Nia', 'Cousin Marcus'],
    personId: null,
  },
  {
    id: 'sample-3',
    title: 'Summer Cookout 2019',
    caption:
      'Big summer cookout in the backyard. Uncle Ray grilled and the kids played basketball until sunset.',
    date: '2019-07-04',
    taggedNames: ['Uncle Ray', 'Cousin Marcus'],
    personId: null,
  },
  {
    id: 'sample-4',
    title: 'Beach Day',
    caption: 'Long warm summer day at the beach with mom and the cousins.',
    date: '2021-08-12',
    taggedNames: ['Mom', 'Cousin Nia'],
    personId: null,
  },
  {
    id: 'sample-5',
    title: 'Maya\'s Graduation',
    caption: 'Maya graduated from high school today. Grandma cried happy tears.',
    date: '2022-06-04',
    taggedNames: ['Maya', 'Grandma Rose'],
    personId: null,
  },
  {
    id: 'sample-6',
    title: 'Thanksgiving Dinner',
    caption: 'Thanksgiving at our place. Aunt Carol brought her sweet potato pie again.',
    date: '2017-11-23',
    taggedNames: ['Aunt Carol', 'Mom', 'Dad'],
    personId: null,
  },
  {
    id: 'sample-7',
    title: 'Reunion 1999',
    caption: 'Family reunion at the old farm. Cousins met cousins for the first time.',
    date: '1999-08-15',
    taggedNames: ['Grandma Rose', 'Uncle Ray'],
    personId: null,
  },
];

const FLAG_LABELS: Record<SemanticFlagKey, { title: string; help: string }> = {
  semanticIndex: {
    title: 'Semantic Index (master)',
    help: 'Master switch. When OFF, every other Phase 6C flag is also disabled, no index is built, no UI renders.',
  },
  smartSearch: {
    title: 'Smart Search',
    help: 'Opt-in semantic search modal that runs entirely on this device.',
  },
  memoryClusters: {
    title: 'Memory Clusters',
    help: 'Groups memories into themes (Christmas, summer cookouts, graduations).',
  },
  historianMode: {
    title: 'Historian Insights',
    help: 'Documentary-style era summaries and big-moment surfacing.',
  },
  relationshipInsights: {
    title: 'Relationship Insights',
    help: 'Per-relative timelines and cross-generation connections.',
  },
  memoryResurfacing: {
    title: 'Memory Resurfacing',
    help: '"Remember this?" anniversary, seasonal, and similar-memory recommendations.',
  },
};

const SANDBOX_ACTIONS = ['search', 'cluster', 'historian'] as const;
type SandboxAction = (typeof SANDBOX_ACTIONS)[number];

const AISemanticLab: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, loading } = useAuth();
  const [, force] = useState(0);
  const [query, setQuery] = useState('cookout summer');
  const [sandboxOutput, setSandboxOutput] = useState<string>('');
  const [busyAction, setBusyAction] = useState<SandboxAction | null>(null);
  const [latency, setLatency] = useState<number | null>(null);

  const flags = useMemo(() => snapshotSemanticFlags(), []);
  const stats = useMemo(() => readSemanticIndexStats(), []);

  const allowed = isOwner || isAdmin;

  useEffect(() => {
    if (!loading && !allowed) {
      navigate('/');
    }
  }, [loading, allowed, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }
  if (!allowed) return null;

  const refresh = () => force((n) => n + 1);

  const refreshedFlags = snapshotSemanticFlags();
  const refreshedStats = readSemanticIndexStats();

  const handleToggle = (k: SemanticFlagKey, value: boolean) => {
    setSemanticFlagLocal(k, value);
    refresh();
  };

  const handleRebuild = () => {
    if (!isSemanticIndexEnabled()) {
      setSandboxOutput(
        'Semantic Index flag is OFF. Toggle it on first to build the local index.',
      );
      return;
    }
    const r = buildSemanticMemoryIndex(SAMPLE_MEMORIES);
    setSandboxOutput(
      r.ok
        ? `Indexed ${r.indexed} sample memories. Cache stored in localStorage only.`
        : `Could not rebuild: ${r.reason}`,
    );
    refresh();
  };

  const handleClear = () => {
    clearSemanticIndex();
    setSandboxOutput('Local semantic cache cleared.');
    refresh();
  };

  const runSandbox = (action: SandboxAction) => {
    setBusyAction(action);
    setLatency(null);
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      if (action === 'search') {
        const r = searchSemanticMemories(query, { topK: 6 });
        setSandboxOutput(
          r.results.length
            ? r.results
                .map(
                  (x) =>
                    `• ${x.memoryId} — score ${x.score.toFixed(2)} (${x.matchedTerms.join(', ') || '—'})`,
                )
                .join('\n')
            : 'No results. Make sure the index has been rebuilt with sample data.',
        );
        setLatency(r.durationMs);
      } else if (action === 'cluster') {
        const c = buildMemoryClusters();
        setSandboxOutput(
          c.length
            ? c
                .map(
                  (x) =>
                    `• ${x.label} (${x.memoryIds.length}) — ${x.topTerms.join(', ')}`,
                )
                .join('\n')
            : 'No clusters yet. Rebuild the index first.',
        );
      } else if (action === 'historian') {
        const h = buildHistorianDashboard();
        const lines: string[] = [];
        lines.push(`Eras: ${h.eras.length}`);
        for (const e of h.eras.slice(0, 3))
          lines.push(`  • ${e.era} (${e.yearRange.from}–${e.yearRange.to}) — ${e.themes.join(', ') || '—'}`);
        lines.push(`Traditions: ${h.traditions.length}`);
        for (const t of h.traditions.slice(0, 3))
          lines.push(`  • ${t.label} ×${t.occurrences}`);
        lines.push(`Big Moments: ${h.moments.length}`);
        for (const m of h.moments.slice(0, 3))
          lines.push(`  • ${m.label} (${m.year ?? '—'})`);
        lines.push(`Cross-gen: ${h.crossGen.length}`);
        setSandboxOutput(lines.join('\n'));
      }
      const end =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (latency === null) setLatency(Math.max(0, Math.round(end - start)));
    } catch (e) {
      setSandboxOutput(
        `Safe error — sandbox could not run: ${(e as Error).message}`,
      );
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-white to-pink-50">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-fuchsia-700" />
              AI Semantic Lab
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Owner/admin diagnostics + sandbox for the Phase 6C semantic memory
              layer. Everything below runs locally on this device — no API
              calls, no Supabase writes.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            Back
          </Button>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Flags persist only in this browser. To enable for all users, set the
            corresponding <span className="font-mono">VITE_ENABLE_SEMANTIC_*</span>{' '}
            environment variable at build time.
          </div>
        </div>

        {/* Index stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Database className="w-4 h-4 text-fuchsia-700" />
              Index Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[11px] uppercase text-slate-500">Indexed</div>
              <div className="font-semibold text-slate-900">
                {refreshedStats.indexed.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Cache size</div>
              <div className="font-semibold text-slate-900">
                {(refreshedStats.cacheBytes / 1024).toFixed(1)} KB
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Built at</div>
              <div className="font-semibold text-slate-900">
                {refreshedStats.builtAt
                  ? new Date(refreshedStats.builtAt).toLocaleString()
                  : 'never'}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Last latency</div>
              <div className="font-semibold text-slate-900">
                {latency === null ? '—' : `${latency}ms`}
              </div>
            </div>
            <div className="col-span-full flex flex-wrap gap-2 pt-2">
              <Button size="sm" onClick={handleRebuild} className="gap-1.5">
                <RefreshCcw className="w-3.5 h-3.5" />
                Rebuild from sample data
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleClear}
                className="gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear local cache
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Flag toggles */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-fuchsia-700" />
              Phase 6C Flags
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(Object.keys(FLAG_LABELS) as SemanticFlagKey[]).map((k) => (
              <div
                key={k}
                className="rounded-lg border border-slate-200 p-3 flex items-start gap-3"
              >
                <Switch
                  checked={refreshedFlags[k]}
                  onCheckedChange={(v) => handleToggle(k, v)}
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                    {FLAG_LABELS[k].title}
                    {refreshedFlags[k] ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                        On
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                        Off
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {FLAG_LABELS[k].help}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Sandbox */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Search className="w-4 h-4 text-fuchsia-700" />
              Sandbox (sample data only)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search query"
              className="h-10"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => runSandbox('search')}
                disabled={busyAction === 'search'}
              >
                Run smart search
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runSandbox('cluster')}
                disabled={busyAction === 'cluster'}
              >
                Build clusters
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runSandbox('historian')}
                disabled={busyAction === 'historian'}
              >
                Run historian preview
              </Button>
            </div>
            <pre className="text-xs bg-slate-900 text-emerald-100 rounded-lg p-3 whitespace-pre-wrap min-h-[120px] max-h-[280px] overflow-auto">
              {sandboxOutput || '// Sandbox output will appear here.'}
            </pre>
            <p className="text-[10px] text-slate-400">
              The sandbox always operates on hard-coded sample memories. It never
              reads or writes your real family data.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AISemanticLab;
